'use client'

import React from 'react'
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

// "Time from detection to protection": share of entries per time bucket,
// one coloured column each. Used by SIBS Custom Mail Alert and Alerts
// Analysis (minute buckets, the defaults below) and CTT Pilot Overview's
// time to takedown (day buckets, via fastKeys/fastLabel/colorFor).

export type LatencyDatum = { key: string; count: number }

const LATENCY_COLORS: Record<string, string> = {
  '<30 min':   'var(--success)',
  '30-60 min': 'var(--success)',
  '1-2h':      '#6ee7b7',
  '2-6h':      '#a7f3d0',
  '>6h':       'var(--warning)',
}

function latencyColor(key: string): string {
  return LATENCY_COLORS[key] ?? (key.includes('3d') ? 'var(--destructive)' : key.includes('24') || key.includes('1-3') ? 'var(--warning)' : 'var(--success)')
}

export function ProtectionLatencyCard({
  data,
  isDark,
  muted,
  text,
  label = 'Time from detection to protection',
  fastKeys = ['<30 min', '30-60 min', '< 1h'],
  fastLabel = 'protected under an hour',
  colorFor = latencyColor,
  subtitle,
  className = 'col-span-1',
}: {
  data: LatencyDatum[]
  isDark: boolean
  muted: string
  text: string
  label?: string
  /** Buckets that count toward the "fast" share line, and its wording —
   * the defaults are SIBS's minute buckets. */
  fastKeys?: string[]
  fastLabel?: string
  colorFor?: (key: string) => string
  /** Extra line under the label (e.g. median and measured count). */
  subtitle?: React.ReactNode
  className?: string
}) {
  const total = data.reduce((s, b) => s + b.count, 0)
  const fastCount = data
    .filter(b => fastKeys.includes(b.key))
    .reduce((s, b) => s + b.count, 0)
  const fastPct = total > 0 ? Math.round((fastCount / total) * 100) : 0
  const chartData = data.map((bucket) => ({
    ...bucket,
    percent: total > 0 ? Math.round((bucket.count / total) * 100) : 0,
  }))
  const tooltipCls = 'border-border bg-card text-muted-foreground'

  return (
    <div className={`${className} flex h-full min-w-0 flex-col overflow-hidden border border-border bg-card`}>
      <div className="flex shrink-0 flex-col gap-0.5 px-4 pt-3 pb-2">
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>{label}</div>
        {subtitle && <div className={`font-mono text-[10px] ${muted}`}>{subtitle}</div>}
        {total > 0 && (
          <div className={`font-mono text-[10px] ${muted}`}>
            <span className={'text-success'}>{fastPct}%</span> {fastLabel}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 4, left: -30, bottom: 4 }} barCategoryGap="20%">
            <XAxis dataKey="key" tick={{ fill: 'var(--subtle-text)', fontSize: 8, fontFamily: 'monospace' }} tickLine={false} axisLine={false} interval={0} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'var(--secondary)' }} content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null
              const p = payload[0]
              return (
                <div className={`rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: colorFor(p.payload.key) }} />
                    <span>{p.payload.key}</span>
                    <span className={`ml-auto pl-4 font-semibold ${text}`}>{p.payload.percent}%</span>
                  </div>
                  <div className={`mt-1 flex items-center justify-between gap-6 ${muted}`}>
                    <span>Actual</span>
                    <span className={`font-semibold ${text}`}>{p.payload.count}</span>
                  </div>
                </div>
              )
            }} />
            <Bar dataKey="percent" radius={[4, 4, 0, 0]} maxBarSize={64} isAnimationActive={false}>
              {chartData.map((b, i) => (
                <Cell key={i} fill={colorFor(b.key)} />
              ))}
              <LabelList
                dataKey="percent"
                position="top"
                formatter={(value) => `${value ?? 0}%`}
                style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, fill: 'var(--foreground)' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
